/**
 * Створення декларації з ПДВ (2026-07-30) — єдина механіка Кабінету ДПС
 * (як 1ДФ/J05): «створити» → документ → чернетка у Журналі Кабінету →
 * підпис КЕП і відправка ТАМ. Ця сторінка — вибір організації і періоду.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { vatDeclarations, type VatAppendixCode } from '@/api/vatDeclarations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

const MONTHS = ['січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

// Додатки Д1–Д6 (2026-08-05, еталон J05): Д1 — авто з ПН, решта — ручні
// бланки на вкладках редактора. Склад можна змінити і після створення.
const ALL_APPENDICES: { code: VatAppendixCode; label: string }[] = [
  { code: 'D1', label: 'Д1 — відомості про ПН (авто: незареєстровані ПН + кредит по постачальниках)' },
  { code: 'D2', label: 'Д2 — розшифровка відʼємного значення (ряд. 19–21)' },
  { code: 'D3', label: 'Д3 — заява про повернення бюджетного відшкодування / коштів СЕА' },
  { code: 'D4', label: 'Д4 — розрахунок сум пільг з ПДВ' },
  { code: 'D5', label: 'Д5 — розрахунок частки використання (ст.199)' },
  { code: 'D6', label: 'Д6 — заява-скарга на постачальника (помилки у ПН)' },
];

export default function VatDeclarationCreatePage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('tax.invoice.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);

  const now = new Date();
  const prevM = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevY = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(prevY);
  const [month, setMonth] = useState(prevM);
  const [reportType, setReportType] =
    useState<'REPORTING' | 'REPORTING_NEW' | 'CORRECTING'>('REPORTING');
  const [apps, setApps] = useState<VatAppendixCode[]>(['D1']);
  // Уточнюючий розрахунок: період, що уточнюється.
  const [corrYear, setCorrYear] = useState(prevY);
  const [corrMonth, setCorrMonth] = useState(prevM);
  const [error, setError] = useState<string | null>(null);
  const isCorrection = reportType === 'CORRECTING';

  const toggleApp = (code: VatAppendixCode) =>
    setApps((a) => a.includes(code) ? a.filter((x) => x !== code) : [...a, code]);

  const effectiveOrg = orgId
    || (availableOrgs.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || availableOrgs[0]?.id || '';

  const create = useMutation({
    mutationFn: () => vatDeclarations.create({
      organization_id: effectiveOrg,
      // УР: period = період ПОДАННЯ (поточний), corrected_* — що уточнюється.
      period_year: isCorrection ? now.getFullYear() : year,
      period_month: isCorrection ? now.getMonth() + 1 : month,
      report_type: reportType,
      // УР: уточнюючі додатки лише Д1–Д5 (Д6-скарга з УР не подається).
      appendices: isCorrection
        ? apps.filter((a) => a !== 'D6') : apps,
      ...(isCorrection
        ? { corrected_period_year: corrYear, corrected_period_month: corrMonth }
        : {}),
    }),
    onSuccess: (doc) => navigate(`/taxation/vat-declarations/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return (
      <div className="card">
        У вас немає прав створювати декларації у жодній з {totalAccessibleOrgs} організацій.
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 620 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нова декларація з ПДВ</h2>
        <button style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet/new')}>до каталогу</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        Рядки розрахуються з регістрів ПДВ автоматично — далі перевірите
        бланк, а підпис і відправка — у Журналі Кабінету ДПС (пакетом з
        додатком Д1, якщо є дані).
      </p>
      <div className="field">
        <label>Організація</label>
        <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
          {availableOrgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Тип</label>
        <select value={reportType}
          onChange={(e) => {
            const t = e.target.value as 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
            setReportType(t);
            // УР: додатки лише за потреби (не навʼязуємо Д1 за замовчуванням).
            setApps(t === 'CORRECTING' ? [] : ['D1']);
          }}>
          <option value="REPORTING">Звітна</option>
          <option value="REPORTING_NEW">Звітна нова</option>
          <option value="CORRECTING">Уточнюючий розрахунок (виправлення помилок)</option>
        </select>
      </div>
      {!isCorrection && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 9' }}>
            <label>Звітний місяць</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Рік</label>
            <input type="number" value={year}
              onChange={(e) => setYear(Number(e.target.value))} />
          </div>
        </div>
      )}
      {isCorrection && (
        <>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 9' }}>
              <label>Період, що УТОЧНЮЄТЬСЯ — місяць</label>
              <select value={corrMonth}
                onChange={(e) => setCorrMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Рік</label>
              <input type="number" value={corrYear}
                onChange={(e) => setCorrYear(Number(e.target.value))} />
            </div>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0 0 0.6rem' }}>
            Гр.4 «показник, який уточнюється» засіється з останньої декларації
            цього періоду у системі; гр.5 правите, гр.6 (різниця) рахується
            автоматично. Подається окремим документом; при недоплаті —
            ряд.18.1 + штраф 3% (ряд.18.2).
          </p>
        </>
      )}
      <div style={{ marginTop: '0.25rem' }}>
        <label style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
          {isCorrection ? 'Уточнюючі додатки (за потреби)' : 'Додатки'}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem',
          marginTop: 4 }}>
          {ALL_APPENDICES
            .filter((a) => !isCorrection || a.code !== 'D6')
            .map((a) => (
            <label key={a.code} style={{ display: 'flex', gap: '0.4rem',
              alignItems: 'center', cursor: 'pointer', fontSize: '0.88rem' }}>
              <input type="checkbox" style={{ width: 'auto' }}
                checked={apps.includes(a.code)}
                onChange={() => toggleApp(a.code)} />
              {a.label}
            </label>
          ))}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.4rem 0 0.6rem' }}>
          Порожній додаток у пакет не вкладається. Склад додатків можна
          змінити і після створення — на сторінці декларації.
        </p>
      </div>
      {error && (
        <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
      )}
      <button className="primary" onClick={() => create.mutate()}
        disabled={create.isPending || !effectiveOrg}>
        {create.isPending ? 'створюється…' : 'створити'}
      </button>
    </div>
  );
}
